import React, { useEffect, useState } from 'react'
import { AdminLayout } from '../components/AdminLayout'
import { adminApi } from '../services/adminApi'
import {
  Users,
  Package,
  Activity,
  HardDrive,
  AlertCircle,
  RefreshCw
} from 'lucide-react'

export const DashboardPage = () => {
  const [stats, setStats] = useState(null)
  const [activity, setActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [statsData, activityData] = await Promise.all([
        adminApi.getDashboardStats(),
        adminApi.getDashboardActivity(20)
      ])
      setStats(statsData)
      setActivity(activityData.activity || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading && !stats) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-500 mt-1">Platform overview and statistics</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error loading dashboard</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Users Card */}
              <StatsCard
                title="Total Users"
                value={stats.users?.total || 0}
                subtitle={`${stats.users?.active_30d || 0} active (30d)`}
                icon={Users}
                color="blue"
              />

              {/* Models Card */}
              <StatsCard
                title="Total Models"
                value={stats.models?.total || 0}
                subtitle={`${stats.models?.recent_uploads_7d || 0} recent (7d)`}
                icon={Package}
                color="green"
              />

              {/* Sessions Card */}
              <StatsCard
                title="User Sessions"
                value={stats.sessions?.total || 0}
                subtitle={`${stats.sessions?.active || 0} active`}
                icon={Activity}
                color="purple"
              />

              {/* Storage Card */}
              <StatsCard
                title="Storage Used"
                value={`${stats.storage?.total_gb || 0} GB`}
                subtitle={`${(stats.storage?.total_bytes || 0) / 1e9} bytes`}
                icon={HardDrive}
                color="orange"
              />
            </div>

            {/* Models by Status */}
            {stats.models?.by_status && Object.keys(stats.models.by_status).length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Models by Status</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(stats.models.by_status).map(([status, count]) => (
                    <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{count}</p>
                      <p className="text-sm text-gray-600 capitalize">{status}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {activity.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No recent activity</div>
            ) : (
              activity.slice(0, 10).map((item, index) => (
                <div key={item.id || index} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.message}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-gray-500">
                          {item.action && <span className="capitalize">{item.action.replace(/_/g, ' ')}</span>}
                        </span>
                        {item.resource_type && (
                          <span className="text-xs text-gray-500">
                            {item.resource_type}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {new Date(item.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {item.changes && item.changes.length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                          {item.changes.slice(0, 2).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

const StatsCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`h-12 w-12 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-600">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  )
}
